import React, { useEffect, useState } from "react";
import { Upload, message } from "antd";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";

/**
 * Generic FileUpload Component
 * 
 * A reusable file upload component that handles validation, file selection,
 * and preview URL management. Designed to be flexible for various use cases.
 * 
 * @param {Object} props
 * @param {Function} props.onFileSelect - Callback function called when a valid file is selected. Receives the file object.
 * @param {string} props.accept - File type acceptance string (default: "image/*")
 * @param {number} props.maxSizeInMB - Maximum file size in MB (default: 5)
 * @param {number} props.maxCount - Maximum number of files (default: 1)
 * @param {boolean} props.disabled - Whether the upload is disabled
 * @param {boolean} props.loading - Whether the upload is in loading state
 * @param {string} props.buttonText - Text to display on button (supports i18n keys)
 * @param {string} props.loadingText - Text to display when loading (supports i18n keys)
 * @param {React.Component} props.icon - Icon component to display in button
 * @param {Object} props.buttonProps - Additional props to pass to Button component
 * @param {Object} props.uploadProps - Additional props to pass to Upload component
 * @param {Function} props.validateFileType - Optional custom file type validation function (file) => boolean
 * @param {Object} props.customErrorMessage - Custom error messages { typeError?: string, sizeError?: string }
 */
export const FileUpload = ({
  onFileSelect,
  accept = "image/*",
  maxSizeInMB = 5,
  maxCount = 1,
  disabled = false,
  loading = false,
  buttonText,
  loadingText,
  icon,
  buttonProps = {},
  uploadProps = {},
  validateFileType,
  customErrorMessage = {},
}) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(null);

  // Clean up object URL on unmount and when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Default file type validation
  const defaultValidateFileType = (file) => {
    if (accept.includes("image/*") || accept.includes("image/")) {
      return file.type.startsWith("image/");
    }
    // For other file types, check if file type matches accept pattern
    const acceptTypes = accept.split(",").map((type) => type.trim());
    return acceptTypes.some((type) => {
      if (type.endsWith("/*")) {
        const baseType = type.slice(0, -2);
        return file.type.startsWith(baseType);
      }
      return file.type === type || file.name.endsWith(type.replace(".", ""));
    });
  };

  // Validate before upload
  const beforeUpload = (file) => {
    // Use custom validation if provided, otherwise use default
    const isValidType = validateFileType
      ? validateFileType(file)
      : defaultValidateFileType(file);

    if (!isValidType) {
      const errorMsg =
        customErrorMessage.typeError ||
        t("FILE_TYPE_ERROR", {
          defaultValue: "You can only upload files of the specified type!",
        });
      message.error(errorMsg);
      return Upload.LIST_IGNORE;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSizeInMB;
    if (!isLtMaxSize) {
      const errorMsg =
        customErrorMessage.sizeError ||
        t("FILE_SIZE_ERROR", {
          maxSize: maxSizeInMB,
          defaultValue: `File must be smaller than ${maxSizeInMB}MB!`,
        });
      message.error(errorMsg);
      return Upload.LIST_IGNORE;
    }

    return false; // Prevent auto upload, we'll handle it manually
  };

  // Handle file change
  const handleChange = ({ fileList: newFileList }) => {
    // Clean up previous preview URL if exists
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Only keep the latest file and call onFileSelect
    const latestFile = newFileList.slice(-1)[0];
    
    if (latestFile) {
      // Create preview URL for images
      const file = latestFile.originFileObj || latestFile;
      if (file && file.type?.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }

      // Call the callback with the selected file
      onFileSelect?.(latestFile);
    } else {
      // No file selected, clean up preview
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      onFileSelect?.(null);
    }
  };

  // Get preview URL for external use if needed
  const getPreviewUrl = () => previewUrl;

  return (
    <Upload
      beforeUpload={beforeUpload}
      type="button"
      maxCount={maxCount}
      accept={accept}
      onChange={handleChange}
      fileList={null}
      disabled={disabled || loading}
      {...uploadProps}
    >
      <Button
        disabled={disabled || loading}
        variant="primary"
        className="btn-xs btn-outline align-self-start"
        {...buttonProps}
      >
        {icon && <HugeiconsIcon icon={icon} />}
        <span>
          {loading
            ? loadingText
              ? t(loadingText)
              : t("UPLOADING")
            : buttonText
            ? t(buttonText)
            : t("UPLOAD_FILE", { defaultValue: "Upload File" })}
        </span>
      </Button>
    </Upload>
  );
};

